import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  ArrowLeft, 
  Sparkles, 
  DatabaseZap, 
  PlusCircle, 
  Laugh, 
  Tag,
  Plus,
  Copy,
  Trash2,
  Target,
  Rabbit,
  BookOpen,
  BarChart3,
  MessageCircle,
  CheckCircle,
  ChevronRight
} from 'lucide-react';
// Import sports icons
import SportsIcons, { 
  Basketball, 
  Football, 
  Tennis, 
  GolfClub, 
  Baseball,
  Soccer,
  Hockey
} from '../../icons/SportsIcons';
import MobileMenuItem from './MobileMenuItem';
import MobileMenuGroup from './MobileMenuGroup';
import ChatSuggestionChips from '../../ChatChips';
import EditableBetSlipItem from '../../virtualBetSlip/EditableBetSlipItem';
import ParlayExporter from '../../virtualBetSlip/ParlayExporter';
import CustomPromptsTab from '../../customPrompts/CustomPromptsTab';
import GameExplorerHub from '../../mobilePanels/GameExplorerHub';
import { getBrazilianStatus } from '../../../utils/detectBrazil';
import { mobileSlideOverMenuPtBR } from '../../mappings/mobileSlideOverMenu.pt-br';

// Simple Toast Component
const Toast = ({ message, isVisible, onClose, type = 'info' }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000); // Auto close after 3 seconds
      
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const getToastStyle = () => {
    switch (type) {
      case 'success':
        return 'bg-emerald-600 border-emerald-500';
      case 'info':
      default:
        return 'bg-blue-600 border-blue-500';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      case 'info':
      default:
        return <MessageCircle className="w-5 h-5" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -50, scale: 0.95 }}
      className={`fixed top-4 left-4 right-4 z-[70] ${getToastStyle()} border rounded-lg p-3 shadow-lg backdrop-blur-sm`}
    >
      <div className="flex items-center gap-3">
        <div className="text-white">
          {getIcon()}
        </div>
        <div className="flex-1">
          <p className="text-white text-sm font-medium">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

// Create memoized sports icons to prevent recreation
const SPORTS_ICONS = {
  NBA: <Basketball className="w-5 h-5" />,
  NHL: <Hockey className="w-5 h-5" />,
  NCAAB: <Basketball className="w-5 h-5" />,
  SOCCER: <Soccer className="w-5 h-5" />,
  GOLF: <GolfClub className="w-5 h-5" />,
  TENNIS: <Tennis className="w-5 h-5" />,
  MLB: <Baseball className="w-5 h-5" />,
  NFL: <Football className="w-5 h-5" />
};


// Animation variants - moved outside to prevent recreation
const ANIMATION_VARIANTS = {
  overlay: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  },
  menu: {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { type: 'spring', damping: 25, stiffness: 300 } }
  },
  slideIn: {
    hidden: { x: '100%' },
    visible: { x: 0, transition: { type: 'spring', damping: 25, stiffness: 300 } },
    exit: { x: '100%', transition: { duration: 0.2 } }
  }
};


/**
 * Enhanced slide-over menu matching desktop functionality
 * Fully optimized with complete implementation
 */
const MobileSlideOverMenu = ({
  userId,
  userStats,
  responseMode,
  updateUIState,
  RESPONSE_MODES,
  handleNewChat,
  onSuggestionClick,
  onShowMobileStats,
  onClose,
  bets,
  setBets,
  // PickSlip functionality
  removeBet,
  updateBet,
  handleClearAll,
  handleConfirmClear,
  handleManualAdd,
  handleExportToClipboard,
  showConfirmClear,
  setShowConfirmClear,
  showAddModal,
  setShowAddModal,
  newPickText,
  setNewPickText,
  deleteTargetId,
  setDeleteTargetId,
  showDeleteConfirm,
  setShowDeleteConfirm,
  handleConfirmDelete,
  customView = null,
  // New props to match desktop
  tier,
  gameUsageState,
  runAutomatedGamePrediction,
  currentGameInfo = null
}) => {
  // Check if user is Brazilian
  const isBrazilian = getBrazilianStatus();
  
  // Translation function
  const t = (text) => {
    if (!isBrazilian) return text;
    return mobileSlideOverMenuPtBR[text] || text;
  };

  // Navigation state
  const [currentView, setCurrentView] = useState(customView || 'main');
  const [menuHistory, setMenuHistory] = useState([customView || 'main']);
  const [activeSuggestionTab, setActiveSuggestionTab] = useState('prompts');

  // Toast state
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'info' });

  // Toast helper functions
  const showToast = useCallback((message, type = 'info') => {
    setToast({ isVisible: true, message, type });
  }, []);

  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, isVisible: false }));
  }, []);


  // Update currentView if customView changes
  useEffect(() => {
    if (customView) {
      setCurrentView(customView);
      setMenuHistory([customView]);
    }
  }, [customView]);

  // Memoized navigation handlers
  const navigateTo = useCallback((view) => {
    setMenuHistory(prev => [...prev, view]);
    setCurrentView(view);
  }, []);

  const navigateBack = useCallback(() => {
    if (menuHistory.length > 1) {
      const newHistory = [...menuHistory];
      newHistory.pop();
      setMenuHistory(newHistory);
      setCurrentView(newHistory[newHistory.length - 1]);
    } else {
      onClose();
    }
  }, [menuHistory, onClose]);


  // Handle response mode selection
  const setResponseMode = useCallback((mode) => {
    updateUIState({ responseMode: mode });
    navigateBack();
  }, [updateUIState, navigateBack]);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback((prompt) => {
    onSuggestionClick(prompt);
    onClose();
  }, [onSuggestionClick, onClose]);

  // Handle prompt selection for custom prompts
  const handlePromptUse = useCallback((promptText) => {
    onSuggestionClick(promptText);
    onClose();
  }, [onSuggestionClick, onClose]);

  // Handle prompt limit reached
  const handlePromptLimitReached = useCallback(() => {
    updateUIState({ showUpgradePrompt: true });
  }, [updateUIState]);

  // Get current selected mode icon - memoized
  const SelectedModeIcon = useMemo(() => {
    return RESPONSE_MODES && responseMode ? RESPONSE_MODES[responseMode]?.icon : Laugh;
  }, [RESPONSE_MODES, responseMode]);


  // Main render content function with all cases
  const renderContent = useCallback(() => {
    switch (currentView) {
      case 'main':
        return (
          <div className="flex flex-col h-full">
            <div className="p-4 border-b border-slate-700">
              <h2 className="text-lg font-medium text-emerald-300">PickStar Tools</h2>
              <p className="text-sm text-slate-400 mt-1">Access powerful features and insights</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <MobileMenuGroup>
                <MobileMenuItem
                  icon={<Sparkles className="w-5 h-5" />}
                  label={t("AI Toolkit")}
                  onClick={() => navigateTo('aiToolkit')}
                  chevron
                />
                
                {/* Game Explorer - Premium Highlighted */}
                <motion.button
                  onClick={() => navigateTo('teamStats')}
                  className="w-full relative overflow-hidden rounded-lg mb-2 group"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  {/* Gradient background with animation */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-emerald-600/20 opacity-70 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-transparent to-emerald-600/10 animate-pulse" />
                  
                  {/* Border glow effect */}
                  <div className="absolute inset-0 rounded-lg border border-emerald-500/30 group-hover:border-emerald-400/50 transition-colors" />
                  
                  <div className="relative flex items-center justify-between p-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 shadow-lg shadow-emerald-500/20">
                        <BarChart3 className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <span className="text-base font-semibold text-white">{t("Game Explorer")}</span>
                        <div className="flex items-center gap-1 mt-0.5">
                          <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                          <span className="text-xs text-emerald-400 font-medium">{t('Live Sports')}</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-emerald-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.button>
              </MobileMenuGroup>
              
              <MobileMenuGroup title="My Tools">
                <MobileMenuItem
                  icon={<Tag className="w-5 h-5" />}
                  label={t("Pick Slip")}
                  onClick={() => navigateTo('pickSlip')}
                  badge={bets.length > 0 ? bets.length : null}
                  chevron
                />
                <MobileMenuItem
                  icon={<SelectedModeIcon className="w-5 h-5" />}
                  label={`${t('Chat Mode:')} ${responseMode === 'normal' ? t('Standard') : responseMode === 'quick' ? t('Quick') : t('Detailed')}`}
                  onClick={() => navigateTo('responseModes')}
                  chevron
                />
              </MobileMenuGroup>
              
              <MobileMenuGroup title="Actions">
                <MobileMenuItem
                  icon={<DatabaseZap className="w-5 h-5" />}
                  label={t("Chat History")}
                  onClick={() => {
                    onShowMobileStats();
                    onClose();
                  }}
                />
                <MobileMenuItem
                  icon={<PlusCircle className="w-5 h-5" />}
                  label={t("New Chat")}
                  onClick={() => {
                    handleNewChat();
                    onClose();
                  }}
                />
              </MobileMenuGroup>
            </div>
          </div>
        );

      case 'aiToolkit':
        return (
          <div className="flex flex-col h-full">
            <div className="p-4 border-b border-slate-700 flex items-center">
              <button 
                onClick={navigateBack}
                className="mr-3 p-1.5 rounded-full hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h2 className="text-lg font-medium text-emerald-300">{t('AI Toolkit')}</h2>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <p className="text-sm text-slate-400 mb-4">
                Use AI-powered suggestions and custom prompts
              </p>
              
              {/* Tabs matching desktop */}
              <div className="flex border-b border-slate-700 mb-4 overflow-x-auto pb-1">
                <button 
                  onClick={() => setActiveSuggestionTab('prompts')}
                  className={`py-2 px-3 text-sm font-medium whitespace-nowrap flex items-center ${
                    activeSuggestionTab === 'prompts' 
                      ? 'text-emerald-400 border-b-2 border-emerald-400' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <BookOpen className="w-4 h-4 mr-1.5" />
                  {t('My Prompts')}
                </button>
                <button 
                  onClick={() => setActiveSuggestionTab('default')}
                  className={`py-2 px-3 text-sm font-medium whitespace-nowrap ${
                    activeSuggestionTab === 'default' 
                      ? 'text-emerald-400 border-b-2 border-emerald-400' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {t('Popular Picks')}
                </button>
                <button 
                  onClick={() => setActiveSuggestionTab('soccer')}
                  className={`py-2 px-3 text-sm font-medium whitespace-nowrap ${
                    activeSuggestionTab === 'soccer' 
                      ? 'text-emerald-400 border-b-2 border-emerald-400' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Soccer
                </button>
                <button 
                  onClick={() => setActiveSuggestionTab('advanced')}
                  className={`py-2 px-3 text-sm font-medium whitespace-nowrap ${
                    activeSuggestionTab === 'advanced' 
                      ? 'text-emerald-400 border-b-2 border-emerald-400' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {t('Advanced')}
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Custom Prompts Tab */}
                {activeSuggestionTab === 'prompts' && (
                  <div>
                    <CustomPromptsTab 
                      userId={userId}
                      onUsePrompt={handlePromptUse}
                      gameInfo={currentGameInfo}
                      onPromptLimitReached={handlePromptLimitReached}
                      onUpgrade={() => updateUIState({ showUpgradePrompt: true })}
                      isMobile={true}
                    />
                  </div>
                )}

                {/* Default suggestions */}
                {activeSuggestionTab === 'default' && (
                  <div>
                    <h3 className="text-sm font-medium text-emerald-400 mb-3">Popular Suggestions</h3>
                    <ChatSuggestionChips 
                      onSuggestionClick={handleSuggestionSelect} 
                      variant="default" 
                      displayMode="uniform" 
                    />
                  </div>
                )}
                
                {/* Soccer suggestions */}
                {activeSuggestionTab === 'soccer' && (
                  <div>
                    <h3 className="text-sm font-medium text-emerald-400 mb-3">Soccer Analysis</h3>
                    <ChatSuggestionChips 
                      onSuggestionClick={handleSuggestionSelect} 
                      variant="soccer" 
                      displayMode="uniform" 
                    />
                  </div>
                )}
                
                {/* Advanced suggestions */}
                {activeSuggestionTab === 'advanced' && (
                  <div>
                    <h3 className="text-sm font-medium text-emerald-400 mb-3">{t('Advanced Analysis')}</h3>
                    <ChatSuggestionChips 
                      onSuggestionClick={handleSuggestionSelect} 
                      variant="advanced" 
                      displayMode="uniform" 
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'teamStats':
        return (
          <GameExplorerHub 
            navigateBack={navigateBack}
            onClose={onClose}
          />
        );

      case 'responseModes':
        return (
          <div className="flex flex-col h-full">
            <div className="p-4 border-b border-slate-700 flex items-center">
              <button 
                onClick={navigateBack}
                className="mr-3 p-1.5 rounded-full hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h2 className="text-lg font-medium text-emerald-300">{t('Response Mode')}</h2>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <p className="text-sm text-slate-400 mb-4">
                Choose how PickStar responds to your questions
              </p>
              {Object.entries(RESPONSE_MODES).map(([mode, { icon: Icon }]) => {
                const isActive = mode === responseMode;
                
                // Match desktop labels
                const label = mode === 'normal' ? t('Balanced Response') : 
                            mode === 'quick' ? t('Quick & Concise') : t('Expert Analysis');

                const description = mode === 'normal' ? t('Standard detail and context') : 
                                  mode === 'quick' ? t('Short, direct answers') : 
                                  t('Comprehensive & detailed insights');
                
                return (
                  <button
                    key={mode}
                    onClick={() => setResponseMode(mode)}
                    className={`w-full text-left p-4 mb-3 flex items-start gap-3 rounded-lg border ${
                      isActive 
                        ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300' 
                        : 'bg-slate-800/50 border-slate-700/50 hover:bg-slate-700/30 text-white/80'
                    } transition-colors`}
                  >
                    <div className={`p-2 rounded-full ${isActive ? 'bg-emerald-900/50' : 'bg-slate-700/50'}`}>
                      <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-400' : 'text-white/70'}`} />
                    </div>
                    <div>
                      <div className="text-base font-medium">{label}</div>
                      <div className="text-sm text-white/60 mt-1">{description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
        
      case 'pickSlip':
        return (
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="p-3 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={navigateBack}
                  className="p-1.5 rounded hover:bg-white/5 text-white/40 hover:text-white/70 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <h2 className="text-base font-semibold text-white">My Picks</h2>
                {bets.length > 0 && (
                  <span className="bg-white/10 text-white/90 text-xs px-2 py-0.5 rounded-full font-medium">
                    {bets.length}
                  </span>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-3">
              {bets.length > 0 ? (
                <div className="space-y-2">
                  {bets.map((bet, index) => (
                    <EditableBetSlipItem
                      key={bet.id}
                      bet={bet}
                      onRemove={(id) => {
                        setDeleteTargetId(id);
                        setShowDeleteConfirm(true);
                      }}
                      onUpdate={updateBet}
                      allBets={bets}
                      index={index}
                      isMobile={true}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-white/40">
                  <p className="text-sm mb-1">No picks yet</p>
                  <p className="text-xs text-white/30">
                    Add bets from AI suggestions
                  </p>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="p-3 border-t border-white/5 bg-white/[0.02]">
              <div className="flex items-center justify-between mb-2">
                <button
                  onClick={() => setShowAddModal(true)}
                  className="text-sm text-white/40 hover:text-white/70 transition-colors"
                >
                  Add Pick
                </button>

                <div className="flex items-center gap-2">
                  {bets.length > 0 && (
                    <>
                      <ParlayExporter bets={bets} dropdownDirection="up" />
                      <button
                        onClick={() => setShowConfirmClear(true)}
                        className="text-sm text-white/40 hover:text-red-400/90 transition-colors"
                      >
                        Clear All
                      </button>
                    </>
                  )}
                </div>
              </div>

              {bets.length > 0 && (
                <button
                  onClick={handleExportToClipboard}
                  className="w-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white py-2.5 px-4 text-sm font-medium rounded transition-colors"
                >
                  Copy All Picks
                </button>
              )}
            </div>
          </div>
        );

        
      default:
        return null;
    }
  }, [
    currentView, 
    navigateTo, 
    bets.length, 
    SelectedModeIcon, 
    responseMode, 
    onShowMobileStats, 
    onClose, 
    handleNewChat,
    navigateBack,
    onSuggestionClick,
    activeSuggestionTab,
    setActiveSuggestionTab,
    userId,
    handlePromptUse,
    currentGameInfo,
    handlePromptLimitReached,
    updateUIState,
    handleSuggestionSelect,
    RESPONSE_MODES,
    setResponseMode,
    bets,
    setDeleteTargetId,
    setShowDeleteConfirm,
    updateBet,
    setShowAddModal,
    setShowConfirmClear,
    handleExportToClipboard
  ]);

  return (
    <>
      {/* Toast Notification */}
      <AnimatePresence>
        <Toast
          message={toast.message}
          isVisible={toast.isVisible}
          onClose={hideToast}
          type={toast.type}
        />
      </AnimatePresence>

      {/* Background overlay */}
      <motion.div 
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[130]"
        variants={ANIMATION_VARIANTS.overlay}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={onClose}
      />
      
      {/* Slide-over panel */}
      <motion.div
        className="fixed inset-y-0 right-0 w-full bg-slate-900 shadow-xl z-[130] flex flex-col"
        variants={ANIMATION_VARIANTS.slideIn}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        {/* Content container */}
        <div className="flex-1 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div 
              key={currentView}
              className="h-full flex flex-col"
              variants={ANIMATION_VARIANTS.slideIn}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>

      {/* MOVED: Close button outside the animated container with highest z-index */}
      {/* Hide when GameExplorerHub is shown since it has its own close button */}
      {currentView !== 'teamStats' && (
        <button 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Close button clicked!'); // Debug log
            onClose();
          }}
          className="fixed top-4 right-4 p-1.5 rounded-full hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors z-[140] pointer-events-auto"
          style={{ right: '4px', top: '16px' }} // Ensure positioning
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      {/* Modals */}
      <AnimatePresence>
        {/* Clear All Confirmation Dialog */}
        {showConfirmClear && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[140] flex items-start justify-center pt-20 px-4"
            onClick={() => setShowConfirmClear(false)}
          >
            <motion.div 
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-lg p-4 w-full max-w-xs"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="text-white text-base font-medium mb-2">Clear all picks?</h4>
              <p className="text-slate-300 text-sm mb-4">
                Are you sure you want to remove all picks from your list?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowConfirmClear(false)}
                  className="flex-1 py-2 border border-slate-600 text-slate-300 rounded text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleClearAll();
                    setShowConfirmClear(false);
                  }}
                  className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white rounded text-sm"
                >
                  Clear All
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {/* Delete Single Pick Confirmation Dialog */}
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[140] flex items-start justify-center pt-20 px-4"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div 
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-lg p-4 w-full max-w-xs"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="text-white text-base font-medium mb-2">Delete this pick?</h4>
              <p className="text-slate-300 text-sm mb-4">
                Are you sure you want to remove this pick from your list?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2 border border-slate-600 text-slate-300 rounded text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white rounded text-sm"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {/* Add Pick Modal */}
        {showAddModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[60] flex items-start justify-center pt-20 px-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div 
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-lg p-4 w-full max-w-xs"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="text-white text-base font-medium mb-3">Add Custom Pick</h4>
              <textarea
                value={newPickText}
                onChange={(e) => setNewPickText(e.target.value)}
                placeholder="Enter your pick details..."
                className="w-full bg-slate-700 border border-slate-600 rounded p-2 text-sm text-slate-200 resize-none h-24 focus:outline-none focus:ring-1 focus:ring-emerald-500 mb-4"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-2 border border-slate-600 text-slate-300 rounded text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleManualAdd}
                  className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-sm"
                  disabled={!newPickText.trim()}
                >
                  Add Pick
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default React.memo(MobileSlideOverMenu);